import { entityEffects, ICurrentEntity, selectCurrentEntity, selectEntityDisplayName } from '@opengov/app-core/entity';
import { coreFeatureFlagEffects } from '@opengov/app-core/featureFlag';
import { ensureAll } from '@opengov/compose-app-router/onEnter';
import { routerActions } from '@opengov/compose-app-router/state';
import { createSelector } from '@opengov/compose-app/reselect';
import { generatePageTitle } from '@opengov/stories-shared/utils';

import { storySetupPdfProps } from '../selectors/storySetupPDFSelector';
import { onEnterStoriesRoutes } from '../../routes/onEnter';
import { maintenancePath, storyListPath } from '../../routes/paths';
import { StorySetupPDFView } from '../components/StoryView/SetupPDF';
import { navigateToStoryView } from '../state';

type NextLocationParams<Params> = {
  params: Params;
};

type OnEnterCallback = () => void;
type OnErrorCallback = (message: string, action: unknown) => void;

type IStorySetupPdfRouteParams = {
  entitySubdomain: string;
  storyId: string;
  publicationId?: string;
};

type StorySetupPdfNextLocation = NextLocationParams<IStorySetupPdfRouteParams>;

export const storySetupPDFRoute = {
  name: 'StorySetupPdf',
  title: createSelector(selectEntityDisplayName, (name) => generatePageTitle('Story Setup PDF', name)),
  selector: storySetupPdfProps,
  component: StorySetupPDFView,
  onEnter: ensureAll([
    onEnterStoriesRoutes,
    async (state, nextLocation: StorySetupPdfNextLocation, onSuccess: OnEnterCallback, onError: OnErrorCallback) => {
      const { entitySubdomain, storyId } = nextLocation.params;
      await entityEffects
        .fetchRequestedOrCurrentEntity({
          requestedEntitySubdomain: entitySubdomain,
          currentEntity: selectCurrentEntity(state)
        })
        .then(async (entity: ICurrentEntity) => {
          if (entity.subdomain !== entitySubdomain) {
            return onError(
              'Entity not found...',
              routerActions.replace.create({ pathname: storyListPath(entity.subdomain) })
            );
          }
          await coreFeatureFlagEffects.getFlags().then(async (flags) => {
            // Feature flag check - uses same flag as Publication Setup PDF
            if (flags && !flags['product-feature-publications-setup-pdf']) {
              navigateToStoryView(entitySubdomain, storyId, false);
            }

            // Maintenance mode check
            if (flags && flags['product-stories-maintenance-window']) {
              return onError(
                'Redirecting to maintenance page...',
                routerActions.replace.create({ pathname: maintenancePath(entitySubdomain) })
              );
            }

            onSuccess();
          });
        });
    }
  ])
};
